import type { CustomerDetails } from '@contract';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { ReasonDialog } from '@/components/reason-dialog';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { callAdmin } from '@/lib/api';
import { useCan } from '@/lib/staff-context';

/** What the super admin's automatic reveal says, so the audit log shows why. */
const AUTO_REASON = 'Super admin viewing this customer';

export interface DetailsReveal {
  details: CustomerDetails | undefined;
  pending: boolean;
  error: string | null;
  reveal: (reason: string) => void;
}

/**
 * A customer's personal details, unmasked. One reveal with one reason shows
 * everything the staff member may see, and is logged. The super admin sees them
 * when the page opens (still logged, with its own reason), because they must
 * see everything.
 */
export function useCustomerDetails(
  customerId: string,
  auto: boolean,
): DetailsReveal {
  const mutation = useMutation({
    mutationFn: (reason: string) =>
      callAdmin('customers.reveal_details', {
        customer_id: customerId,
        reason,
      }),
  });
  const asked = useRef(false);
  const { mutate } = mutation;
  useEffect(() => {
    if (!auto || asked.current) return;
    asked.current = true;
    mutate(AUTO_REASON);
  }, [auto, mutate]);
  return {
    details: mutation.data,
    pending: mutation.isPending,
    error: mutation.error?.message ?? null,
    reveal: mutate,
  };
}

/** The button and its reason prompt, for staff who may reveal and have not yet. */
export function RevealButton({
  reveal,
  label = 'Reveal personal details',
}: Readonly<{ reveal: DetailsReveal; label?: string }>) {
  const canReveal = useCan('customers.reveal_details');
  const [open, setOpen] = useState(false);
  if (!canReveal || reveal.details) return null;
  return (
    <>
      <Button
        variant="outline"
        size="sm"
        pending={reveal.pending}
        onClick={() => setOpen(true)}
      >
        {label}
      </Button>
      <ReasonDialog
        open={open}
        onOpenChange={setOpen}
        title="Reveal this customer's personal details"
        description="Shows their name, phone and the rest of what you may see, for this view only. The reveal and its reason are logged against you."
        confirmLabel="Reveal"
        pending={reveal.pending}
        error={reveal.error}
        onConfirm={(reason) => {
          reveal.reveal(reason);
          setOpen(false);
        }}
      />
    </>
  );
}

const FIELDS: { key: keyof CustomerDetails; label: string }[] = [
  { key: 'full_name', label: 'Full name' },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email' },
  { key: 'region', label: 'Region' },
  { key: 'country_of_residence', label: 'Country of residence' },
  { key: 'gender', label: 'Gender' },
  { key: 'date_of_birth', label: 'Date of birth' },
  { key: 'document_type', label: 'ID document' },
  { key: 'issuing_country', label: 'Issued in' },
  { key: 'id_number', label: 'ID number' },
  { key: 'id_expiry', label: 'ID expires' },
];

/** Everything we hold about who the customer is, once revealed. */
export function DetailsCard({ reveal }: Readonly<{ reveal: DetailsReveal }>) {
  const shown = FIELDS.filter(
    (field) => reveal.details && field.key in reveal.details,
  );
  return (
    <Card>
      <CardHeader>
        <CardTitle>Personal details</CardTitle>
        <CardDescription>
          {reveal.details
            ? 'Unmasked for this view. The reveal is logged against you.'
            : 'Masked until you reveal them. One reason shows everything you may see.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {reveal.details ? (
          <dl className="grid grid-cols-[minmax(8rem,auto)_1fr] gap-x-6 gap-y-2 text-sm">
            {shown.map((field) => (
              <div key={field.key} className="contents">
                <dt className="text-muted-foreground">{field.label}</dt>
                <dd className="font-medium">
                  {String(reveal.details?.[field.key] ?? '') || '—'}
                </dd>
              </div>
            ))}
          </dl>
        ) : (
          <div>
            <RevealButton reveal={reveal} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
